<script>
import { Search as SearchIcon } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import DialogWrapper from "@/shared/ui/custom/components/DialogWrapper.vue";
import dateFormat from "@/shared/lib/date-format";

import {
  getInheritanceList,
  updateInheritanceNote,
  updateInheritanceDescription,
} from "@/entities/inheritance/api";
import { getReferenceListByCodes } from "@/entities/directory";
import CreateAction from "@/features/create-action/ui/CreateAction.vue";
import {
  StatusCode,
  ensureStatusDictionary,
  getStatusDictionary,
  getStatusLabel as getStatusLabelFromDict,
} from "@/shared/config/statusDictionary";
import StatusDot from "@/shared/ui/status-dot/StatusDot.vue";

import Button from "@/shared/ui/button/Button.vue";
import { Badge } from "@/shared/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import TableRowActions from "@/shared/ui/custom/components/TableRowActions.vue";
import HereditaryCaseFilter from "./components/HereditaryCaseFilter.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
export default {
  name: "HereditaryCaseList",
  components: {
    CreateAction,
    TableFooterWrapper,
    DialogWrapper,
    SearchIcon,
    Input,
    Button,
    TableRowActions,
    Badge,
    Popover,
    PopoverTrigger,
    PopoverContent,
    HereditaryCaseFilter,
    StatusDot,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
  },
  data() {
    return {
      loading: false,
      list: [],
      organizations: null,
      roles: null,
      total: 0,

      filters: {
        q: "",
        page: 1,
        size: 20,
        number: "",
        notary_uuid: "",
        notary_identifier: "",
        identifier: "",
        deceased_iin: "",
        deceased_fio: "",
        date_from: "",
        date_to: "",
        status__in: [],
        order_by: "",
      },

      showFilters: false,
      pageSizes: [1, 5, 10, 20],
      showCreateNotarial: false,

      showNoteDialog: false,
      currentNote: "",
      currentItemId: null,

      showDescriptionDialog: false,
      currentDescription: "",

      references: [],
    };
  },
  computed: {
    routeName() {
      return this.$route.name;
    },

    notaryData() {
      return this.$store.getters.get_notary || {};
    },

    statusOptions() {
      const dict = getStatusDictionary().value;
      if (dict.size > 0) {
        return Array.from(dict.values()).map((s) => ({
          value: s.code,
          label: s.label,
        }));
      }
      return Object.keys(StatusCode).map((key) => ({
        value: StatusCode[key],
        label: StatusCode[key],
      }));
    },
    actions() {
      return [
        {
          label: "Примечание",
          type: "default",
          action: "note",
          icon: "huge-text-config",
        },
        {
          label: "Краткое содержание",
          type: "default",
          action: "summary",
          icon: "huge-document-info",
        },
        {
          label: "Просмотр действия",
          type: "default",
          action: "view",
          icon: "huge-eye",
        },
      ];
    },
    canCreate() {
      return this.$store.getters.has_access("ICND");
    },
  },
  watch: {
    filters: {
      handler() {
        this.getInheritanceList();
      },
      deep: true,
    },
  },
  mounted() {
    this.getInheritanceList();
    ensureStatusDictionary();
  },
  methods: {
    async getInheritanceList() {
      let params = {
        size: this.filters.size,
        page: this.filters.page,
      };

      if (this.notaryData?.uuid) {
        params["notary_uuid"] = this.notaryData.uuid;
      }
      if (this.filters.q) {
        params.q = this.filters.q.trim().toLowerCase();
      }
      if (this.filters.number) params.number = this.filters.number;
      if (this.filters.notary_identifier)
        params.notary_identifier = this.filters.notary_identifier;
      if (this.filters.identifier) params.identifier = this.filters.identifier;
      if (this.filters.deceased_iin)
        params.deceased_iin = this.filters.deceased_iin;
      if (this.filters.deceased_fio)
        params.deceased_fio = this.filters.deceased_fio;
      if (this.filters.date_from) params.date_from = this.filters.date_from;
      if (this.filters.date_to) params.date_to = this.filters.date_to;
      if (this.filters.status__in?.length)
        params.status__in = this.filters.status__in.join(",");
      if (this.filters.order_by) params.order_by = this.filters.order_by;
      this.loading = true;

      try {
        let data = await getInheritanceList(params);

        if (data?.success) {
          if (data.payload) {
            this.total = data.payload?.total;
            this.filters.size = data.payload?.size;
            this.filters.page = data.payload?.page;
            this.list = data.payload.items || [];

            const codes = [
              ...new Set(
                this.list
                  .map((item) => item.template_code)
                  .filter((code) => code != null)
              ),
            ];
            if (codes.length > 0) {
              const refData = await getReferenceListByCodes({ codes });
              if (refData?.success && refData.payload?.items?.length) {
                this.references = refData.payload.items;
              }
            }
          }
        }
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.filters.page = page;
    },
    goToCreate() {
      this.showCreateNotarial = true;
    },
    dateFormat(date, format = "DD.MM.YYYY,  HH:mm") {
      if (!date) {
        return "-";
      }
      return dateFormat.timestamp_to_local_tz(date, format);
    },
    async handleActions(item) {
      if (item.action === "edit") {
        this.$router.push({
          name: "hereditary_case_edit",
          params: { id: item.uuid },
        });
      } else if (item.action === "view") {
        this.$router.push({
          name: "hereditary_case_view",
          params: { id: item.uuid },
        });
      } else if (item.action === "summary") {
        this.openNoteDialog(item.uuid);
      } else if (item.action === "note") {
        this.openDescriptionDialog(item.uuid);
      }
    },

    openNoteDialog(id) {
      const foundItem = this.list.find((item) => item.id === id);
      this.currentItemId = id;
      this.currentNote = foundItem?.note || "";
      this.showNoteDialog = true;
    },
    closeNoteDialog() {
      this.showNoteDialog = false;
      this.currentNote = "";
      this.currentItemId = null;
    },
    async saveNote() {
      try {
        const data = await updateInheritanceNote(
          this.currentItemId,
          this.currentNote
        );
        if (data?.success) {
          await this.getInheritanceList();
        }
      } catch (e) {
        console.error("Ошибка сохранения:", e);
      } finally {
        this.closeNoteDialog();
      }
    },
    openDescriptionDialog(id) {
      const foundItem = this.list.find((item) => item.id === id);
      this.currentItemId = id;
      this.currentDescription = foundItem?.description || "";
      this.showDescriptionDialog = true;
    },
    closeDescriptionDialog() {
      this.showDescriptionDialog = false;
      this.currentDescription = "";
      this.currentItemId = null;
    },
    async saveDescription() {
      try {
        const data = await updateInheritanceDescription(
          this.currentItemId,
          this.currentDescription
        );
        if (data?.success) {
          await this.getInheritanceList();
        }
      } catch (e) {
        console.error("Ошибка сохранения:", e);
      } finally {
        this.closeDescriptionDialog();
      }
    },
    handleRowClick(row) {
      const isReadOnly =
        row.status === StatusCode.COMPLETED ||
        row.status === StatusCode.CANCELLED;

      this.$router.push({
        name: isReadOnly ? "hereditary_case_view" : "hereditary_case_edit",
        params: { id: row.id },
      });
    },
    search() {
      this.filters.page = 1;
    },
    getActions(row) {
      const actions = [...this.actions];
      if (
        row.status === StatusCode.PROJECT &&
        this.$store.getters.has_access("IUND")
      ) {
        actions.unshift({
          label: "Редактировать",
          type: "default",
          action: "edit",
          icon: "huge-pen2",
        });
      }
      return actions;
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    resetFilters() {
      this.filters.number = "";
      this.filters.notary_uuid = "";
      this.filters.notary_identifier = "";
      this.filters.identifier = "";
      this.filters.deceased_iin = "";
      this.filters.deceased_fio = "";
      this.filters.date_from = "";
      this.filters.date_to = "";
      this.filters.status__in = [];
      this.filters.order_by = "";
      this.filters.page = 1;
    },
    getReferenceTitle(code) {
      if (!code) return "-";
      if (this.references?.length === 0) return code;
      const reference = this.references.find((item) => item.code === code);
      return reference?.name || code;
    },
    getStatusLabel(item) {
      const code =
        typeof item?.status === "string"
          ? item.status
          : item?.status?.code || item?.status?.name || "";
      return getStatusLabelFromDict(code) || "-";
    },
    getStatusCode(item) {
      return typeof item?.status === "string"
        ? item.status
        : item?.status?.code || "";
    },
  },
};
</script>

<template>
  <div class="wrapper-page">
    <div class="flex flex-col h-full w-full gap-8">
      <div
        v-if="routeName === 'hereditary_case_list'"
        class="flex flex-col gap-4"
      >
        <div class="flex justify-between gap-5">
          <div class="flex items-center gap-3">
            <div class="relative w-80">
              <SearchIcon
                class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none"
              />
              <Input
                v-model="filters.q"
                :placeholder="$t('Поиск')"
                class="pl-9"
                @input="search"
              />
            </div>
            <Button variant="outline" @click="toggleFilters" size="icon">
              <svg-icon
                name="huge-filter"
                class="w-5 h-5 text-muted-foreground"
              />
            </Button>
          </div>
          <Button v-if="canCreate" variant="default" @click="goToCreate">
            {{ $t("Открыть наследственное дело") }} +
          </Button>
        </div>

        <HereditaryCaseFilter
          v-if="showFilters"
          v-model="filters"
          :statusOptions="statusOptions"
          @change="search"
          @reset="resetFilters"
        />
      </div>

      <div class="table-area">
        <div class="h-full overflow-auto">
          <Table :loading="loading" :scroll-reset-key="filters.page">
            <TableHeader>
              <TableRow>
                <TableHead class="w-[68px]"></TableHead>
                <TableHead class="w-[80px]">{{ $t("ID") }}</TableHead>
                <TableHead class="w-[200px]">{{
                  $t("№ насл. дела")
                }}</TableHead>
                <TableHead class="min-w-[160px]">{{
                  $t("Дата создания")
                }}</TableHead>
                <TableHead class="min-w-[180px]">{{
                  $t("Дата поступления")
                }}</TableHead>
                <TableHead class="min-w-[300px]">{{
                  $t("ФИО умершего")
                }}</TableHead>
                <TableHead class="min-w-[121px]">{{
                  $t("Дата смерти")
                }}</TableHead>

                <TableHead class="flex-1 min-w-[360px]">{{
                  $t("ФИО заявителей")
                }}</TableHead>
                <TableHead class="min-w-[280px]">{{
                  $t("Код шаблона")
                }}</TableHead>
                <TableHead class="min-w-[180px]">{{ $t("Статус") }}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableEmpty v-if="!loading && !list.length" :colspan="11">
                {{ $t("Нет данных") }}
              </TableEmpty>
              <TableRow
                v-for="row in list"
                :key="row.id"
                class="cursor-pointer"
                @click="handleRowClick(row)"
              >
                <TableCell>
                  <TableRowActions
                    :actions="getActions(row)"
                    @action="
                      (item) =>
                        handleActions({
                          action: item.action,
                          uuid: row.id,
                          rowData: row,
                        })
                    "
                  />
                </TableCell>
                <TableCell>{{ row?.id || "-" }}</TableCell>
                <TableCell>{{ row?.number }}</TableCell>
                <TableCell>{{ dateFormat(row?.created_at) }}</TableCell>
                <TableCell>{{ dateFormat(row?.registration_date) }}</TableCell>
                <TableCell>{{ row?.deceased_fio || "-" }}</TableCell>
                <TableCell>{{
                  dateFormat(row?.deceased_death_date, "DD.MM.YYYY")
                }}</TableCell>
                <TableCell>
                  <div
                    v-if="
                      Array.isArray(row?.applicants_fio) &&
                      row.applicants_fio.length > 0
                    "
                    class="flex items-center gap-2"
                  >
                    <span>{{ row.applicants_fio[0] }}</span>
                    <Popover v-if="row.applicants_fio.length > 1">
                      <PopoverTrigger as-child>
                        <div class="trigger" @click.stop>
                          <Badge variant="secondary">
                            +{{ row.applicants_fio.length - 1 }}
                          </Badge>
                        </div>
                      </PopoverTrigger>
                      <PopoverContent class="w-[272px] p-0">
                        <ul class="content">
                          <li
                            v-for="(name, idx) in row.applicants_fio.slice(1)"
                            :key="idx"
                            class="popover-items"
                          >
                            {{ name }}
                          </li>
                        </ul>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <span v-else>-</span>
                </TableCell>

                <TableCell>{{
                  getReferenceTitle(row?.template_code)
                }}</TableCell>
                <TableCell>
                  <div class="flex items-center gap-2">
                    <StatusDot :code="getStatusCode(row)" />
                    <span>{{ getStatusLabel(row) }}</span>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <TableFooterWrapper
          v-if="total > filters.size"
          :total="total"
          v-model:pageSize="filters.size"
          v-model:currentPage="filters.page"
          :pageSizes="[1, 5, 10, 20]"
          :page-size="filters.size"
        >
          <template #pagination>
            <Pagination
              :total="total"
              :items-per-page="filters.size"
              :page="filters.page"
              :sibling-count="1"
              :disabled="loading"
              show-edges
              @update:page="handlePageChange"
            >
              <PaginationContent v-slot="{ items }">
                <PaginationPrevious />
                <template v-for="(item, index) in items" :key="index">
                  <PaginationItem
                    v-if="item.type === 'page'"
                    :value="item.value"
                    :is-active="item.value === filters.page"
                  />
                  <PaginationEllipsis v-else />
                </template>
                <PaginationNext />
              </PaginationContent>
            </Pagination>
          </template>
        </TableFooterWrapper>
      </div>
    </div>
    <router-view></router-view>
    <CreateAction
      v-if="showCreateNotarial"
      key="hereditary-case"
      title="Открыть наследственное дело"
      :predefined-categories="['SVID_PRAVO_NASLED']"
      route-name="hereditary_case_new"
      @close="showCreateNotarial = false"
    />

    <DialogWrapper
      v-model="showNoteDialog"
      v-model:content="currentNote"
      title="Краткое содержание"
      @close="closeNoteDialog"
      @save="saveNote"
    />

    <DialogWrapper
      v-model="showDescriptionDialog"
      v-model:content="currentDescription"
      title="Примечание"
      @close="closeDescriptionDialog"
      @save="saveDescription"
    />
  </div>
</template>

<style scoped lang="scss">
.next {
  @apply gap-4 border border-border;
  display: flex;
  padding: 4px 4px 4px 8px;
  align-items: center;
  justify-content: center;
  height: 32px;
  border-radius: 8px;
  background-color: hsl(var(--muted));
}
.arrow-right {
  @apply border border-border;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px !important;

  height: 24px;
  border-radius: 4px;
  background-color: hsl(var(--card));

  svg {
    width: 12px;
    height: 12px;
  }
}

.filter-icon {
  @apply p-2 rounded-md border border-solid border-border bg-card cursor-pointer flex items-center justify-center;
  &:hover {
    @apply bg-muted;
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.trigger {
  @apply inline-flex items-center gap-1 cursor-pointer select-none;
}
.content {
  @apply p-2 space-y-2 rounded-md bg-card flex flex-col overflow-y-auto;
  max-height: 400px !important;
  width: 400px;
}
.popover-items {
  @apply flex justify-between items-center px-3 py-1 text-foreground text-sm;
}
</style>
